import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { change } from 'redux-form';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import Tooltip from 'components/common/Tooltip';
import IconButton from 'components/common/IconButton';
import IconMoveVert from 'components/icons/IconMoveVert';
import IconMoreVert from 'components/icons/IconMoreVert';
import PrescriberCreateEditDialog from 'components/settings/PrescriberCreateEditDialog';
import PrescribersSearchDialog from 'components/settings/PrescribersSearchDialog';
import IconLink from 'components/icons/IconLink';
import Link from 'components/common/Link';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import splitLongWords from 'utils/splitLongWords';
import formatPrescriberName from 'utils/formatPrescriberName';

import * as normalize from 'utils/normalize';

const styles = theme => ({
  gridItemShrink: {
    flexShrink: 1,
  },
  gridItem: {
    display: 'flex',
    minHeight: 48,
    alignItems: 'center',
  },
  gridItemLink: {
    minWidth: 24,
  },
  gridItemNPI: {
    minWidth: 130,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      paddingRight: 8,
    },
  },
  gridItemLIC: {
    minWidth: 116,
  },
  gridItemPhone: {
    width: 135,
    flexWrap: 'wrap',
  },
  typographyPecos: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    marginLeft: 4,
    marginRight: 4,
    color: theme.palette.common.white,
    cursor: 'pointer',
    backgroundColor: theme.palette.validation['500'],
  },
  typographyPecosEligible: {
    backgroundColor: theme.palette.add['500'],
  },
  typographyPecosInvalid: {
    backgroundColor: theme.palette.notification['500'],
  },
  typographyCaption: {
    display: 'inline-block',
    marginRight: 8,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  iconLink: {
    color: theme.palette.primary[500],
    marginBottom: -4,
  },
  tooltipList: {
    textAlign: 'left',
  },
  buttonMove: {
    position: 'relative',
    top: 1,
  },
  iconMoveVert: {
    width: 10,
    height: 16,
  },
  desktopOnly: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
});

class PrescribersItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      editDialogOpen: false,
      deleteMenuOpen: false,
      searchDialogOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onEditMenuItemClick = this.onEditMenuItemClick.bind(this);
    this.onEditDialogClose = this.onEditDialogClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onSearchDialogClose = this.onSearchDialogClose.bind(this);
    this.onPrescriberSelect = this.onPrescriberSelect.bind(this);
    this.onPrescriberSearchButtonClick = this.onPrescriberSearchButtonClick.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onEditMenuItemClick() {
    this.onMenuClose();
    this.setState({ editDialogOpen: true });
  }

  onEditDialogClose() {
    this.setState({ editDialogOpen: false });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { onRemove } = this.props;
    onRemove();
    this.setState({ anchorEl: null });
  }

  onSearchDialogClose() {
    this.setState({ searchDialogOpen: false });
  }

  onPrescriberSelect(values) {
    const { ID, actions } = this.props;
    const newState = {
      searchDialogOpen: false,
      editDialogOpen: true,
    };
    if (values) {
      const form = `prescriberEdit-${ID}`;
      const primaryAddress = values.addresses[0];
      const primaryTaxonomy = values.taxonomies[0];
      const phone = primaryAddress.phone.indexOf('+1 ') !== -1 ? primaryAddress.phone.replace('+1 ', '') : primaryAddress.phone;
      actions.change(form, 'First Name', values.first_name);
      actions.change(form, 'Last Name', values.last_name);
      actions.change(form, 'NPI', values.npi.toString());
      actions.change(form, 'LIC', primaryTaxonomy.license);
      actions.change(form, 'Phone', normalize.phone(phone));
      if (values.name_prefix) {
        actions.change(form, 'Name Prefix', values.name_prefix);
      }
      if (values.name_suffix && values.name_suffix !== '--') {
        actions.change(form, 'Name Suffix', values.name_suffix);
      }
      if (values.credential) {
        actions.change(form, 'Credential', values.credential);
      }
      actions.change(form, 'address.Street', primaryAddress.address);
      actions.change(form, 'address.City', primaryAddress.city);
      actions.change(form, 'address.State', primaryAddress.state);
      actions.change(form, 'address.Zip Code', normalize.zip(primaryAddress.postal_code));
      actions.change(form, 'taxonomies', values.taxonomies.map(item => ({
        Code: item.code,
        Description: item.desc,
        'Is Primary': item.primary,
        ...item.state ? { State: item.state } : {},
        ...item.license ? { License: item.license } : {},
      })));
    }
    this.setState(newState);
  }

  onPrescriberSearchButtonClick() {
    this.setState({
      editDialogOpen: false,
      searchDialogOpen: true,
    });
  }

  render() {
    const {
      Email, Phone, Fax, NPI, LIC, DEA, ID, Taxonomies, dragHandleClassName, dragDisabled, dragged, classes, ...otherProps
    } = this.props;
    const {
      anchorEl,
      deleteMenuOpen,
      editDialogOpen,
      searchDialogOpen,
    } = this.state;
    const open = Boolean(anchorEl);
    return (
      <Grid
        className={classes.root}
        container
        spacing={0}
        wrap="nowrap"
        justify="space-between"
        alignItems="flex-start"
      >
        <Grid item className={classes.gridItem}>
          <IconButton
            className={classNames(classes.buttonMove, dragHandleClassName)}
            disabled={dragDisabled}
            tooltipContent={(!dragDisabled && !dragged) ? 'Drag it to Change Position' : null}
          >
            <IconMoveVert className={classes.iconMoveVert} />
          </IconButton>
        </Grid>
        <Grid item xs={6} className={classNames(classes.gridItem, classes.gridItemShrink)}>
          <Link
            onClick={this.onEditMenuItemClick}
            breakWord
            size="md"
          >         
            {splitLongWords(formatPrescriberName(otherProps))}
            <Tooltip
              content={
                <Typography>
                  {otherProps['Pecos Status'] === 'Active' && 'PECOS enrolled'}
                  {otherProps['Pecos Status'] === 'Inactive' && 'PECOS not enrolled'}
                  {otherProps['Pecos Status'] === 'Invalid' && 'Mistake in Prescriber information for PECOS'}
                </Typography>
              }
              placement="top"
            >
              <Typography
                className={classNames(classes.typographyPecos, {
                  [classes.typographyPecosEligible]: otherProps['Pecos Status'] === 'Active',
                  [classes.typographyPecosInvalid]: otherProps['Pecos Status'] === 'Invalid',
                })}
              >
                P
              </Typography>
            </Tooltip>
          </Link>
        </Grid>
        <Grid item className={classNames(classes.gridItem, classes.gridItemNPI)}>
          {NPI &&
            <Typography
              color="secondary"
            >
              <Typography
                component="span"
                color="secondary"
                className={classes.typographyCaption}
              >
                NPI:
              </Typography>
              {NPI}
            </Typography>
          }
        </Grid>
        <Grid item className={classNames(classes.gridItem, classes.gridItemLIC, classes.desktopOnly)}>
          {LIC &&
            <Typography
              color="secondary"
            >
              <Typography
                component="span"
                color="secondary"
                className={classes.typographyCaption}
              >
                LIC:
              </Typography>
              {LIC}
            </Typography>
          }
        </Grid>
        <Grid item className={classNames(classes.gridItem, classes.gridItemPhone, classes.desktopOnly)}>
          <Typography
            color="secondary"
          >
            {Phone}
          </Typography>
          {Email &&
            <Typography
              color="secondary"
            >
              {splitLongWords(Email)}
            </Typography>
          }
        </Grid>
        <Grid item className={classNames(classes.gridItem, classes.desktopOnly)}>
          <Typography
            color="secondary"
          >
            {(Taxonomies && Taxonomies[0]) && Taxonomies[0].Description}
          </Typography>
        </Grid>
        <Grid item className={classNames(classes.gridItem, classes.gridItemLink)}>
          {(this.props['Referral Source'] && this.props['Referral Source'].length > 0) &&
            <Tooltip
              content={
                <div className={classes.tooltipList}>
                  <Typography variant="caption" gutterBottom>
                    Linked Referral Source:
                  </Typography>
                  {this.props['Referral Source'].map(item => (
                    <Typography key={item.ID}>
                      {item['Name of source']}
                    </Typography>
                  ))}
                </div>
              }
              placement="top"
            >
              <IconLink className={classes.iconLink} />
            </Tooltip>
          }
        </Grid>
        <Grid item className={classes.gridItem}>
          <IconButton
            aria-label="More"
            aria-owns={open ? `${ID}-prescriber-more-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert
              className={classes.iconMoreVert}
            />
          </IconButton>
          <Menu
            id={`${this.props.ID}-prescriber-more-menu`}
            anchorEl={this.state.anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list={!deleteMenuOpen}
          >
            {deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to remove this prescriber?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
                submitButtonText="Remove"
              />
            ) : (
              <Fragment>
                <MenuItem
                  onClick={this.onEditMenuItemClick}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  color="warning"
                  onClick={this.onDeleteMenuItemClick}
                >
                  Remove
                </MenuItem>
              </Fragment>
            )}
          </Menu>
          <PrescriberCreateEditDialog
            form={`prescriberEdit-${ID}`}
            open={editDialogOpen}
            ID={ID}
            initialValues={{
              'First Name': otherProps['First Name'],
              'Last Name': otherProps['Last Name'],
              Phone,
              ...otherProps['Cell Phone'] ? { 'Cell Phone': otherProps['Cell Phone'] } : {},
              ...Fax ? { Fax } : {},
              ...Email ? { Email } : {},
              NPI,
              ...LIC ? { LIC } : {},
              ...DEA ? { DEA } : {},
              ...otherProps['Name Prefix'] ? { 'Name Prefix': otherProps['Name Prefix'] } : {},
              ...otherProps['Name Suffix'] ? { 'Name Suffix': otherProps['Name Suffix'] } : {},
              ...otherProps['Credential'] ? { Credential: otherProps['Credential'] } : {},
              address: {
                ...otherProps['Address Information'],
              },
              taxonomies: Taxonomies,
              'Referral Source': this.props['Referral Source'].map(item => ({
                value: item.ID,
                label: item['Name of source'],
              })),
            }}
            destroyOnUnmount={!searchDialogOpen}
            onClose={this.onEditDialogClose}
            onSearchButtonClick={this.onPrescriberSearchButtonClick}
          />
          <PrescribersSearchDialog
            form={`prescriberEditRegistrySearch-${ID}`}
            initialValues={{
              'First Name': otherProps['First Name'],
              'Last Name': otherProps['Last Name'],
              NPI,
            }}
            open={searchDialogOpen}
            clearOnOpen={false}
            onClose={this.onSearchDialogClose}
            onPrescriberSelect={this.onPrescriberSelect}
          />
        </Grid>
      </Grid>
    );
  }
}

PrescribersItem.propTypes = {
  ID: PropTypes.number.isRequired,
  'First Name': PropTypes.string.isRequired,
  'Last Name': PropTypes.string.isRequired,
  Email: PropTypes.string,
  Fax: PropTypes.string,
  NPI: PropTypes.string,
  LIC: PropTypes.string,
  DEA: PropTypes.string,
  Phone: PropTypes.string,
  'Pecos Check': PropTypes.bool,
  Taxonomies: PropTypes.arrayOf(PropTypes.shape({
    Description: PropTypes.string,
  })),
  onRemove: PropTypes.func.isRequired,
  actions: PropTypes.shape({
    change: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    iconMoreVert: PropTypes.string.isRequired,
    typographyPecos: PropTypes.string.isRequired,
    typographyPecosEligible: PropTypes.string.isRequired,
    typographyCaption: PropTypes.string.isRequired,
  }).isRequired,
};

PrescribersItem.defaultProps = {
  Fax: null,
  Email: null,
  LIC: null,
  DEA: null,
  NPI: null,
  Phone: null,
  'Pecos Check': false,
  Taxonomies: [],
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ change }, dispatch),
});

export default connect(null, mapDispatchToProps)(withStyles(styles)(PrescribersItem));



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/Prescribers/PrescribersItem.js